import React, { useEffect, useState } from "react";

import SwiperCore from "swiper";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Products } from "../../types/types";
import { BookingPopup } from "../BookingPopup/BookingPopup";

import {
  Button,
  ButtonContainer,
  Card,
  CardHeader,
  Category,
  CategoryContainer,
  Circle,
  Description,
  Divider,
  Meeting,
  Price,
  PriceContainer,
  Tag,
  Title,
  TitleContainer,
  Wrapper
} from "./styles";

import "swiper/css";
import "swiper/css/pagination";

type TariffSectionProps = {
  products: Products[];
  color?: string;
  getAccentColor?: (index: number, product: Products) => string | undefined;
};

export const TariffSection: React.FC<TariffSectionProps> = ({
  products,
  color,
  getAccentColor,
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Products | null>(null);

  const hidePopup = () => setPopupVisible(false);
  const showPopup = (product: Products) => {
    setSelectedProduct(product);
    setPopupVisible(true);
  };

  useEffect(() => {
    if (!swiperInstance) return;

    if (popupVisible) {
      swiperInstance.autoplay?.stop();
    } else {
      swiperInstance.autoplay?.start();
    }
  }, [popupVisible, swiperInstance]);

  const strMaxLength = Math.max(...products.map(
    (c)=>String(c.max_price).length + String(c.min_price).length
  )
  )

  return (
    <Wrapper>
      <Swiper
        modules={[Pagination, Autoplay]}
        pagination={{ dynamicBullets: true }}
        autoplay={{ delay: 5000 }}
        onSwiper={setSwiperInstance}
        spaceBetween={0}
        slidesPerView="auto"
        allowTouchMove={true}
      >
        {products.map((product, index) => {
          const baseColor = (product.title || "").toLowerCase().includes("c-level")
            ? "#B8F4C5"
            : color;
          const accentColor = getAccentColor?.(index, product);
          const itemColor = accentColor ?? baseColor;

          const { price, min_price, max_price, title } = product

          const isLongTitle = title.length > 36;
          const price_ = new Intl.NumberFormat("ru-RU").format(price);
          let min_price_ = new Intl.NumberFormat("ru-RU").format(min_price)
          let max_price_ = new Intl.NumberFormat("ru-RU").format(max_price)

          if (price_ > "0") {
            min_price_ = "0"
            max_price_ = "0"
          }

          return (
            <SwiperSlide
              key={product.id}
            >
              <Card>
                <CardHeader color={itemColor} />
                <CategoryContainer>
                  <Circle color={itemColor} />
                  <Category>{product.product_category.title}</Category>
                </CategoryContainer>
                <TitleContainer>
                  <Title
                    customFontSize={isLongTitle}
                    color={accentColor}
                  >
                    {product.title}
                  </Title>
                </TitleContainer>
                <Divider />
                <Description>{product.description}</Description>
                <Divider />
                <PriceContainer>
                  <Meeting>{product.duration_text}</Meeting>
                  <Price textLength={strMaxLength}>
                    {price ? price_ : ""}
                    {min_price_ != "0" ? " от " + min_price_ : ""}
                    {max_price_ != "0" ? " до " + max_price_ : ""} ₽</Price>
                  <Tag>{product.tag}</Tag>
                </PriceContainer>
                <ButtonContainer>
                  <Button
                    color={accentColor}
                    onClick={() => showPopup(product)}>
                    <span>Заказать</span>
                  </Button>
                </ButtonContainer>
              </Card>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {popupVisible && selectedProduct && (
        <BookingPopup
          visible={true}
          onClose={hidePopup}
          product={selectedProduct}
        />
      )}
    </Wrapper>
  );
};
